import * as XLSX from "xlsx";
import { generateInternalEan13 } from "../../../utils/internalEan13";

const BARCODE_LENGTH = 13;
const ID_HEADER_ALIASES = ["_id", "itemid", "item_id", "id"];
const BRAND_HEADER_ALIASES = ["brandname", "brand_name"];
const SALT_HEADER_ALIASES = ["saltname", "salt_name"];
const MANUFACTURER_HEADER_ALIASES = ["manufacturer"];
const OTC_HEADER_ALIASES = ["isotc", "is_otc"];

const REGENERATE_TOKEN = "REGENERATE";

const isValidImportBarcode = (value) => {
	const trimmed = String(value ?? "").trim();
	if (!trimmed || !/^\d+$/.test(trimmed)) return false;
	return trimmed.length === BARCODE_LENGTH;
};

const BARCODE_HEADER = "barcode";

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[\s_-]+/g, "");

const normalizeKeyPart = (value) =>
	String(value ?? "")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

export const buildImportItemKey = ({ brandName, saltName, manufacturer, isOTC }) => {
	const otc =
		isOTC === true ||
		isOTC === 1 ||
		String(isOTC ?? "")
			.trim()
			.toLowerCase() === "true";

	return [
		normalizeKeyPart(brandName),
		normalizeKeyPart(saltName),
		normalizeKeyPart(manufacturer),
		otc ? "1" : "0",
	].join("|");
};

export const findBarcodeHeaderRowIndex = (rows = []) => {
	for (let index = 0; index < rows.length; index += 1) {
		const headers = (rows[index] || []).map(normalizeHeader);
		if (headers.includes(BARCODE_HEADER)) {
			return index;
		}
	}
	return -1;
};

const findColumnIndex = (headers = [], aliases = []) => {
	const normalized = headers.map(normalizeHeader);
	return normalized.findIndex((header) => aliases.includes(header));
};

const isRowEmpty = (row = []) =>
	!row.some((cell) => String(cell ?? "").trim() !== "");

const getCell = (row, index) => (index >= 0 ? String(row[index] ?? "").trim() : "");

const createUniqueBarcode = (usedBarcodes, clinicId) => {
	for (let attempt = 0; attempt < 100; attempt += 1) {
		const candidate = generateInternalEan13(clinicId);
		if (!usedBarcodes.has(candidate)) {
			return candidate;
		}
	}
	throw new Error("Unable to generate a unique barcode");
};

const buildExistingItemLookups = (existingItems = []) => {
	const byId = new Map();
	const byKey = new Map();
	const usedBarcodes = new Set();

	existingItems.forEach((item) => {
		if (!item) return;
		if (item._id) byId.set(String(item._id), item);
		byKey.set(
			buildImportItemKey({
				brandName: item.brandName,
				saltName: item.saltName,
				manufacturer: item.manufacturer,
				isOTC: item.isOTC,
			}),
			item
		);
		const barcode = String(item.barcode ?? "").trim();
		if (barcode) usedBarcodes.add(barcode);
	});

	return { byId, byKey, usedBarcodes };
};

const resolveExistingItem = (row, headers, lookups) => {
	const idIndex = findColumnIndex(headers, ID_HEADER_ALIASES);
	const idValue = getCell(row, idIndex);
	if (idValue && lookups.byId.has(idValue)) {
		return lookups.byId.get(idValue);
	}

	const brandName = getCell(row, findColumnIndex(headers, BRAND_HEADER_ALIASES));
	const saltName = getCell(row, findColumnIndex(headers, SALT_HEADER_ALIASES));
	const manufacturer = getCell(row, findColumnIndex(headers, MANUFACTURER_HEADER_ALIASES));
	const isOTC = getCell(row, findColumnIndex(headers, OTC_HEADER_ALIASES));

	if (!brandName && !saltName) return null;

	return lookups.byKey.get(
		buildImportItemKey({ brandName, saltName, manufacturer, isOTC })
	);
};

const createEmptySummary = () => ({
	generated: 0,
	keptExisting: 0,
	fixedInvalid: 0,
	regeneratedDuplicate: 0,
	rowsProcessed: 0,
});

/**
 * Mutates rows in place and returns enrichment summary.
 */
export const enrichImportRows = (rows, { clinicId, existingItems = [] }) => {
	const summary = createEmptySummary();
	const errors = [];

	if (!Array.isArray(rows) || rows.length === 0) {
		return { error: "The Excel file is empty.", summary, errors };
	}

	const headerRowIndex = findBarcodeHeaderRowIndex(rows);
	if (headerRowIndex < 0) {
		return {
			error: "Barcode column not found. Please use the exported Excel template.",
			summary,
			errors,
		};
	}

	const headers = rows[headerRowIndex].map((cell) => String(cell ?? "").trim());
	const barcodeColIndex = findColumnIndex(headers, [BARCODE_HEADER]);
	const lookups = buildExistingItemLookups(existingItems);
	const usedBarcodes = new Set(lookups.usedBarcodes);

	for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex += 1) {
		const row = rows[rowIndex] || [];
		if (isRowEmpty(row)) continue;

		summary.rowsProcessed += 1;
		const existingItem = resolveExistingItem(row, headers, lookups);
		let barcode = getCell(row, barcodeColIndex);

		if (barcode.toUpperCase() === REGENERATE_TOKEN) {
			barcode = "";
		}

		if (!barcode) {
			if (existingItem) {
				summary.keptExisting += 1;
				continue;
			}
			const generated = createUniqueBarcode(usedBarcodes, clinicId);
			row[barcodeColIndex] = generated;
			usedBarcodes.add(generated);
			summary.generated += 1;
			continue;
		}

		const validationError = isValidImportBarcode(barcode) ? "" : "invalid";
		if (validationError) {
			if (existingItem?.barcode) {
				row[barcodeColIndex] = existingItem.barcode;
				usedBarcodes.add(existingItem.barcode);
				summary.fixedInvalid += 1;
			} else {
				const generated = createUniqueBarcode(usedBarcodes, clinicId);
				row[barcodeColIndex] = generated;
				usedBarcodes.add(generated);
				summary.fixedInvalid += 1;
			}
			continue;
		}

		if (usedBarcodes.has(barcode)) {
			if (existingItem?.barcode === barcode) {
				summary.keptExisting += 1;
				continue;
			}
			const generated = createUniqueBarcode(usedBarcodes, clinicId);
			row[barcodeColIndex] = generated;
			usedBarcodes.add(generated);
			summary.regeneratedDuplicate += 1;
			continue;
		}

		usedBarcodes.add(barcode);
	}

	return { summary, errors };
};

export const buildImportEnrichmentMessage = (summary, t = (value) => value) => {
	const parts = [];
	if (summary.generated > 0) {
		parts.push(t("{{count}} new barcode(s) generated", { count: summary.generated }));
	}
	if (summary.fixedInvalid > 0) {
		parts.push(t("{{count}} invalid barcode(s) corrected", { count: summary.fixedInvalid }));
	}
	if (summary.regeneratedDuplicate > 0) {
		parts.push(
			t("{{count}} duplicate barcode(s) replaced", { count: summary.regeneratedDuplicate })
		);
	}
	if (summary.keptExisting > 0) {
		parts.push(
			t("{{count}} existing item(s) left unchanged", { count: summary.keptExisting })
		);
	}
	return parts.join(". ");
};

/**
 * Read Excel file, enrich barcode column, return a new File for upload.
 */
export const enrichImportExcelBarcodes = async ({ file, clinicId, existingItems = [] }) => {
	const buffer = await file.arrayBuffer();
	const workbook = XLSX.read(buffer, { type: "array" });
	const sheetName = workbook.SheetNames[0];
	const worksheet = workbook.Sheets[sheetName];
	const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

	const result = enrichImportRows(rows, { clinicId, existingItems });
	if (result.error) {
		return { file, summary: result.summary, errors: [result.error] };
	}

	const nextSheet = XLSX.utils.aoa_to_sheet(rows);
	workbook.Sheets[sheetName] = nextSheet;

	const isXls = file.name.toLowerCase().endsWith(".xls");
	const outBuffer = XLSX.write(workbook, {
		bookType: isXls ? "xls" : "xlsx",
		type: "array",
	});

	const enrichedFile = new File([outBuffer], file.name, {
		type:
			file.type ||
			(isXls
				? "application/vnd.ms-excel"
				: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
	});

	return {
		file: enrichedFile,
		summary: result.summary,
		errors: result.errors,
	};
};
